import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import MLIcon from '../../../../MLIcon/MLIcon';
import styles from './SortButton.module.css';

/**
 * Sort button for assignment header cells.
 */
export function SortButton({ columnId, sortState, onSort, assignmentName }) {
  const handleClick = useCallback(
    (e) => {
      // Stop propagation so the parent cell click doesn't trigger side panel
      e.stopPropagation();
      onSort(columnId);
    },
    [columnId, onSort],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onSort(columnId);
      }
    },
    [columnId, onSort],
  );

  // Determine icon type and aria label based on sort state
  let iconType;
  let ariaLabel;

  if (sortState === 'asc') {
    iconType = 'sorter_caret_up';
    ariaLabel = `${assignmentName} column sorted ascending. Click to sort descending`;
  } else if (sortState === 'desc') {
    iconType = 'sorter_caret_down';
    ariaLabel = `${assignmentName} column sorted descending. Click to remove sorting`;
  } else {
    iconType = 'sorter_caret_up_down';
    ariaLabel = `Sort ${assignmentName} column`;
  }

  return (
    <button
      type="button"
      className={`${styles.sortButton} ${sortState ? styles.active : ''}`}
      tabIndex={-1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel}
      data-testid={`gradebook-sort-assignmentSorter`}
    >
      <MLIcon
        type={iconType}
        width="18"
        height="18"
        viewBox="0 0 18 18"
        className={styles.icon}
        ariaHidden={true}
      />
    </button>
  );
}

SortButton.propTypes = {
  columnId: PropTypes.string.isRequired,
  sortState: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onSort: PropTypes.func.isRequired,
  assignmentName: PropTypes.string,
};

SortButton.defaultProps = {
  sortState: false,
  assignmentName: 'column',
};
